import { useContext, useEffect } from "react";
import Footer from "../../../components/layout/Footer/Footer";
import Navbar from "../../../components/layout/Navbar/Navbar";
import { ThemeContext } from "../../../context/ThemeContext";
import "./BlogDetails.css";
import { Calendar, Clock, Share2, ChevronLeft } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { BlogPost, getBlogPostById } from "../data/posts";

const getAbsoluteUrl = (path: string) => new URL(path, window.location.origin).href;

const updateBlogPreviewMeta = (post: BlogPost | undefined, imageSrc: string | undefined) => {
  if (typeof document === "undefined") {
    return;
  }

  const title = post ? `${post.title} | Squaay Blog` : "Article not found | Squaay Blog";
  const description =
    post?.excerpt || "Read the latest food stories, vendor tips, and Squaay updates.";
  const image = imageSrc ? getAbsoluteUrl(imageSrc) : getAbsoluteUrl("/assets/preview-blog.png");
  const url = window.location.href;

  const upsertMeta = (attribute: "name" | "property", key: string, content: string) => {
    let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);

    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute(attribute, key);
      document.head.appendChild(meta);
    }

    meta.setAttribute("content", content);
  };

  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }

  document.title = title;
  canonical.href = url;

  upsertMeta("name", "description", description);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:image", image);
  upsertMeta("property", "og:image:secure_url", image);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:type", post ? "article" : "website");
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", image);
};

const BlogDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { theme } = useContext(ThemeContext);
  const post = getBlogPostById(id);
  const postImageSrc = post?.image.startsWith("./")
    ? post.image.replace(/^\./, "")
    : post?.image;

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    alert("Link copied to clipboard!");
  };

  updateBlogPreviewMeta(post, postImageSrc);

  useEffect(() => {
    updateBlogPreviewMeta(post, postImageSrc);
  }, [post, postImageSrc]);

  if (!post) {
    return (
      <div className={`blog-details-page ${theme}`}>
        <Navbar />
        <div className="blog-details-header">
          <div className={`blog-details-container ${theme}`}>
            <button className={`back-button ${theme}`} onClick={() => navigate("/blog")}>
              <ChevronLeft size={20} />
              Back to Blog
            </button>
          </div>
        </div>
        <section className={`blog-hero-section ${theme}`}>
          <div className="blog-details-container">
            <article className="blog-article">
              <header className="article-header">
                <h1 className={`article-title ${theme}`}>Article not found</h1>
                <p className={`article-excerpt ${theme}`}>
                  The article you are looking for is unavailable or has moved.
                </p>
              </header>
            </article>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`blog-details-page ${theme}`}>
      <Navbar />

      {/* Back Button */}
      <div className="blog-details-header">
        <div className={`blog-details-container ${theme}`}>
          <button className={`back-button ${theme}`} onClick={() => navigate("/blog")}>
            <ChevronLeft size={20} />
            Back to Blog
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className={`blog-hero-section ${theme}`}>
        <div className="blog-details-container">
          <article className="blog-article">
            {/* Article Header */}
            <header className="article-header">
              <div className="article-category">{post.category}</div>
              <h1 className={`article-title ${theme}`}>{post.title}</h1>
              <p className={`article-excerpt ${theme}`}>{post.excerpt}</p>

              <div className="article-meta">
                <div className="meta-right">
                  <div className="meta-item">
                    <Calendar size={18} />
                    <span>{post.date}</span>
                  </div>
                  <div className="meta-item">
                    <Clock size={18} />
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </div>
            </header>

            {/* Featured Image */}
            <div className="article-image">
              <img src={postImageSrc} alt={post.title} />
            </div>

            {/* Article Content */}
            <div className={`article-content ${theme}`}>
              <div dangerouslySetInnerHTML={{ __html: post.content }} />
            </div>

            {/* Article Footer */}
            <footer className="article-footer">
              <div className="article-actions">
                {/* <button
                  className={`action-button ${liked ? "active" : ""}`}
                  onClick={handleLike}
                >
                  <Heart size={20} fill={liked ? "currentColor" : "none"} />
                  <span>{likes} Likes</span>
                </button>
                <button className="action-button">
                  <MessageCircle size={20} />
                  <span>0 Comments</span>
                </button> */}
                <button className={`action-button ${theme}`} onClick={handleShare}>
                  <Share2 size={20} />
                  <span>Share</span>
                </button>
              </div>

              <div className="article-tags">
                {post.tags.map((tag) => (
                  <span key={tag} className="tag">
                    {tag}
                  </span>
                ))}
              </div>
            </footer>
          </article>
        </div>
      </section>

      {/* Related Articles */}
      {/* <section className="related-section">
        <div className="blog-details-container">
          <h2 className="related-title">More Articles</h2>
          <div className="related-grid">
            {post.related.map(article => (
              <article key={article.id} className="related-card">
                <div className="related-image">
                  <img src={article.image} alt={article.title} />
                </div>
                <div className="related-content">
                  <span className="related-category">{article.category}</span>
                  <h3 className="related-heading">{article.title}</h3>
                  <p className="related-excerpt">{article.excerpt}</p>
                  <a href="#read" className="related-link">
                    Read More <ArrowRight size={16} />
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section> */}

      {/* Newsletter CTA */}
      {/* <section className="newsletter-cta">
        <div className="blog-details-container">
          <div className="cta-content">
            <h2>Get More Insights</h2>
            <p>
              Subscribe to our newsletter for weekly tips and strategies for
              growing your food business
            </p>

            <form className="cta-form" onSubmit={(e) => e.preventDefault()}>
              <input type="email" placeholder="your@email.com" required />
              <button type="submit">Subscribe</button>
            </form>
          </div>
        </div>
      </section> */}

      <Footer />
    </div>
  );
};

export default BlogDetails;
